import React, { useMemo, useState } from "react";
import "./SuggestionForm.css";
import config from "../config";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";

// Helpers to render dynamic questions from JSON
const Field = ({ q, value, onChange }) => {
  const id = q.id || q.name;
  const label = q.label || q.question || q.name;
  const type = (q.type || "text").toLowerCase();
  const required = !!q.required;

  if (type === "textarea" || type === "free" || type === "free_response") {
    return (
      <label className="sf-field">
        <span>
          {label}
          {required ? " *" : ""}
        </span>
        <textarea
          id={id}
          value={value || ""}
          required={required}
          onChange={(e) => onChange(id, e.target.value)}
        />
      </label>
    );
  }

  if (type === "checkboxes") {
    const options = q.options || [];
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="sf-field">
        <span className="sf-label">
          {label}
          {required ? " *" : ""}
        </span>
        <div className="sf-checkboxes">
          {options.map((opt) => (
            <label key={String(opt.value ?? opt)} className="sf-checkbox">
              <input
                type="checkbox"
                checked={selected.includes(opt.value ?? opt)}
                onChange={(e) => {
                  const val = opt.value ?? opt;
                  const next = e.target.checked
                    ? [...selected, val]
                    : selected.filter((v) => v !== val);
                  onChange(id, next);
                }}
              />
              <span>{opt.label ?? opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (type === "radio" || type === "multiple_choice") {
    const options = q.options || [];
    return (
      <div className="sf-field">
        <span className="sf-label">
          {label}
          {required ? " *" : ""}
        </span>
        <div className="sf-radios">
          {options.map((opt) => (
            <label key={String(opt.value ?? opt)} className="sf-radio">
              <input
                type="radio"
                name={id}
                checked={(value ?? "") === (opt.value ?? opt)}
                onChange={() => onChange(id, opt.value ?? opt)}
              />
              <span>{opt.label ?? opt}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (type === "select") {
    const options = q.options || [];
    return (
      <label className="sf-field">
        <span>
          {label}
          {required ? " *" : ""}
        </span>
        <select
          id={id}
          value={value ?? ""}
          required={required}
          onChange={(e) => onChange(id, e.target.value)}
        >
          <option value="" disabled>
            {q.placeholder || "Select..."}
          </option>
          {options.map((opt) => (
            <option key={String(opt.value ?? opt)} value={opt.value ?? opt}>
              {opt.label ?? opt}
            </option>
          ))}
        </select>
      </label>
    );
  }

  // default text input
  return (
    <label className="sf-field">
      <span>
        {label}
        {required ? " *" : ""}
      </span>
      <input
        id={id}
        type={q.inputType || "text"}
        value={value || ""}
        required={required}
        placeholder={q.placeholder || ""}
        onChange={(e) => onChange(id, e.target.value)}
      />
    </label>
  );
};

const SuggestionForm = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [initialData, setInitialData] = useState({});
  const [followupSchema, setFollowupSchema] = useState(null);
  const [followupData, setFollowupData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const initialQuestions = useMemo(
    () => [
      {
        id: "description",
        label: "Please describe the AI tool request",
        type: "textarea",
        required: true,
      },
      {
        id: "department",
        label: "What department are you in?",
        type: "checkboxes",
        required: true,
        options: [
          "Account Services",
          "Content Management",
          "Creative",
          "DM",
          "Finance",
          "Marketing",
          "Public Relations",
          "SEO",
          "Strategy",
          "Traditional Media",
          "UX",
        ],
      },
      {
        id: "frequency",
        label: "Frequency of the Task",
        type: "multiple_choice",
        required: true,
        options: ["Daily", "Weekly", "Monthly", "Ad Hoc"],
      },
      {
        id: "time_before",
        label: "Estimated Time Spent on the Task Before the Tool (Weekly)",
        type: "text",
        required: true,
      },
      {
        id: "num_users",
        label: "Estimated Number of People that would use the tool",
        type: "text",
        required: true,
      },
      {
        id: "has_current_tool",
        label: "Is there a tool/process currently being used for this task?",
        type: "multiple_choice",
        required: true,
        options: ["Yes", "No"],
      },
      {
        id: "current_tool_name",
        label: "If Yes, What tool?",
        type: "text",
        required: false,
        showIf: { field: "has_current_tool", equals: "Yes" },
      },
    ],
    []
  );

  const handleInitialChange = (id, val) =>
    setInitialData((p) => ({ ...p, [id]: val }));
  const handleFollowupChange = (id, val) =>
    setFollowupData((p) => ({ ...p, [id]: val }));

  const submitInitial = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const url =
        config?.n8n?.suggestion?.step1Url ||
        process.env.REACT_APP_N8N_STEP1_URL;
      if (!url) throw new Error("Missing n8n step 1 URL");
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: initialData }),
      });
      if (!res.ok) throw new Error(`Step 1 failed (${res.status})`);
      const json = await res.json();
      // Normalize possible formats from AI: either an array of question objects
      // or { questions: [...] }. Map to our internal shape: { id, label, type, required, options? }
      const toSlug = (text) =>
        String(text || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      const mapType = (t) => {
        const v = String((t || "text").toLowerCase());
        if (v === "checkbox") return "checkboxes";
        if (v === "multiple_choice" || v === "radio") return "multiple_choice";
        if (v === "textarea" || v === "free" || v === "free_response")
          return "textarea";
        return v; // "text" or others
      };
      const rawQuestions = Array.isArray(json)
        ? json
        : Array.isArray(json?.questions)
        ? json.questions
        : [];
      const normalized = rawQuestions.map((q, idx) => {
        const label =
          q.label || q.question || q.question_text || `Question ${idx + 1}`;
        const id = q.id || q.name || toSlug(label) || `q${idx + 1}`;
        const type = mapType(q.type || q.question_type || "text");
        let options = q.options || [];
        // Ensure options are strings or {label,value} pairs acceptable by our Field
        if (Array.isArray(options)) {
          options = options.map((opt) => {
            if (typeof opt === "string") return opt;
            if (opt && typeof opt === "object") {
              return {
                label: opt.label ?? String(opt.value ?? ""),
                value: opt.value ?? opt.label ?? "",
              };
            }
            return String(opt ?? "");
          });
        } else {
          options = [];
        }
        return {
          id,
          label,
          type,
          required: Boolean(q.required),
          options,
        };
      });
      setFollowupSchema({ questions: normalized });
      setStep(2);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const submitFinal = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const url =
        config?.n8n?.suggestion?.step2Url ||
        process.env.REACT_APP_N8N_STEP2_URL;
      if (!url) throw new Error("Missing n8n step 2 URL");

      // Build combined Q/A with question text for both sections
      const initialEntries = initialQuestions
        .filter((q) => {
          if (!q.showIf) return true;
          return initialData[q.showIf.field] === q.showIf.equals;
        })
        .map((q) => ({
          id: q.id,
          question: q.label,
          type: q.type || "text",
          answer: initialData[q.id],
        }));

      const followupEntries = (followupSchema?.questions || []).map((q) => ({
        id: q.id,
        question: q.label,
        type: q.type || "text",
        answer: followupData[q.id],
      }));

      const combined = [...initialEntries, ...followupEntries];

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ combined, userEmail: user?.email || null }),
      });
      if (!res.ok) throw new Error(`Step 2 failed (${res.status})`);
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout>
        <div className="sf-container">
          <h2>Thank you!</h2>
          <p>Your submission has been sent. We'll follow up soon.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="sf-container">
        <h2>Suggestion Box</h2>
        {error && <div className="sf-error">{error}</div>}
        {step === 1 &&
          (submitting ? (
            <div className="sf-loading" role="status" aria-live="polite">
              <div className="sf-spinner" />
              <div>Generating follow-up questions</div>
            </div>
          ) : (
            <form onSubmit={submitInitial} className="sf-form">
              {initialQuestions.map((q) => {
                if (q.showIf) {
                  const shouldShow =
                    initialData[q.showIf.field] === q.showIf.equals;
                  if (!shouldShow) return null;
                }
                return (
                  <Field
                    key={q.id}
                    q={q}
                    value={initialData[q.id]}
                    onChange={handleInitialChange}
                  />
                );
              })}
              <div className="sf-actions">
                <button type="submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          ))}

        {step === 2 && (
          <form onSubmit={submitFinal} className="sf-form">
            {(followupSchema?.questions || []).map((q) => (
              <Field
                key={q.id || q.name}
                q={q}
                value={followupData[q.id || q.name]}
                onChange={handleFollowupChange}
              />
            ))}
            <div className="sf-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                Back
              </button>
              <button type="submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default SuggestionForm;
